import { RefObject, useEffect, useRef, useState } from 'react';
import { EstateType, StatusId } from './Enums';
import { MyPagesEstateDto, User } from './services/generatedApi';
import Cookies from 'js-cookie';
import { FocalCoordinates, Crop } from './types/UmbracoOfficeInterface';
import * as L from 'leaflet';

export function useDebounce<T>(value: T, delay: number): T {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

export function toggleInList<T>(item: T, list: T[]): T[] {
  return list.includes(item) ? list.filter((i) => i !== item) : [...list, item];
}

export function useOnClickOutside(
  ref: RefObject<HTMLElement>,
  handler: () => void
): void {
  useEffect(
    () => {
      const listener = (event: Event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return;
        }
        handler();
      };
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
}

export function useKeyPress(targetKey: string): boolean {
  // State for keeping track of whether key is pressed
  const [keyPressed, setKeyPressed] = useState<boolean>(false);
  // If pressed key is our target key then set to true

  // Add event listeners
  useEffect(() => {
    function downHandler({ key }: { key: string }) {
      if (key === targetKey) {
        setKeyPressed(true);
      }
    }
    // If released key is our target key then set to false
    const upHandler = ({ key }: { key: string }) => {
      if (key === targetKey) {
        setKeyPressed(false);
      }
    };

    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, [targetKey]); // Empty array ensures that effect is only run on mount and unmount
  return keyPressed;
}

//Sets ariaattributes
export function changeCheckboxAriaAttr(
  e: React.ChangeEvent<HTMLInputElement>
): void {
  const item = e.currentTarget;

  switch (item?.getAttribute('aria-checked')) {
    case 'true':
      item.setAttribute('aria-checked', 'false');
      break;
    case 'false':
      item.setAttribute('aria-checked', 'true');
      break;
  }
}

export function gtmPageBuilder(officeName: string | undefined): string {
  let pageLink = '';
  if (officeName !== undefined) {
    officeName = officeName.toLowerCase().trim();

    const a =
      'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìıİłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
    const b =
      'aaaaaaaaaacccddeeeeeeeegghiiiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
    const p = new RegExp(a.split('').join('|'), 'g');

    officeName = officeName
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'
      .replace(/[^\w\-]+/g, '') // Remove all non-word characters
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, ''); // Trim - from end of text

    pageLink = window.location.href + 'tack/' + officeName;
  }
  return pageLink;
}

export function getSmallObjectTypeIconPath(estateType: EstateType): string {
  let estateIcon = '';
  if (estateType === EstateType.House) {
    estateIcon = 'liten_villa.svg';
  } else if (estateType === EstateType.Plot) {
    estateIcon = 'liten_tomt.svg';
  } else if (estateType === EstateType.Commercial) {
    estateIcon = 'liten_kommersiell.svg';
  } else if (estateType === EstateType.Cottage) {
    estateIcon = 'liten_fritidshus.svg';
  } else if (estateType === EstateType.Cooperative) {
    estateIcon = 'liten_bostadsratt.svg';
  } else if (estateType === EstateType.Agriculture) {
    estateIcon = 'liten_gard.svg';
  } else if (estateType === EstateType.Project) {
    estateIcon = 'liten_nyproduktion.svg';
  } else if (estateType === EstateType.OwnedApartment) {
    estateIcon = 'liten_bostadsratt.svg';
  }
  return estateIcon;
}

export function getLargeObjectTypeIconPath(
  estateType: EstateType,
  color?: 'white' | 'blue'
): string {
  let estateIcon = '';
  if (estateType === EstateType.House) {
    estateIcon = color === 'white' ? `stor_villa-white.svg` : 'stor_villa.svg';
  } else if (estateType === EstateType.Plot) {
    estateIcon = color === 'white' ? `stor_tomt-white.svg` : 'stor_tomt.svg';
  } else if (estateType === EstateType.Commercial) {
    estateIcon =
      color === 'white' ? `stor_kommersiell-white.svg` : 'stor_kommersiell.svg';
  } else if (estateType === EstateType.Cottage) {
    estateIcon =
      color === 'white'
        ? `stor_fritidshus-white.svg`
        : 'stor_fritidshus_blue.svg';
  } else if (estateType === EstateType.Cooperative) {
    estateIcon =
      color === 'white' ? `stor_bostadsratt-white.svg` : 'stor_bostadsratt.svg';
  } else if (estateType === EstateType.Agriculture) {
    estateIcon = color === 'white' ? `stor_gard-white.svg` : 'stor_gard.svg';
  } else if (estateType === EstateType.Project) {
    estateIcon =
      color === 'white'
        ? `stor_nyproduktion-white.svg`
        : 'stor_nyproduktion.svg';
  } else if (estateType === EstateType.OwnedApartment) {
    estateIcon =
      color === 'white' ? `stor_bostadsratt-white.svg` : 'stor_bostadsratt.svg';
  } else if (estateType === EstateType.RowHouse) {
    estateIcon =
      color === 'white' ? `stor_parhus-white.svg` : 'stor_parhus.svg';
  }
  return estateIcon;
}

//Formatting for example 1000000 to 1 000 000
export function formatLargeNumbers(n: number): string {
  if (n === 0) {
    return '0';
  }
  const formattedPrice = [];
  for (; n >= 1000; n = Math.floor(n / 1000)) {
    formattedPrice.unshift(String(n % 1000).padStart(3, '0'));
  }
  formattedPrice.unshift(n);
  const finalOutput = formattedPrice.join(' ');
  return finalOutput;
}

export function makeCamelCase(input: string): string {
  return input?.replace(
    /(?:^\w|[a-zåäö]|\b\w)/g,
    function (word: string, index: number) {
      return index === 0 ? word.toUpperCase() : word.toLowerCase();
    }
  );
}

//Made for Autocomplete of search estates field.
export function handleUpAndDown(event: KeyboardEvent): void {
  if (event.key === 'ArrowUp') {
    const parent = document.getElementById('ac-result-list') as HTMLElement;
    if (parent) {
      const theList = getAllFocusableElements(parent);
      const arrayOfFocusableElements = Array.from(theList) as HTMLElement[];
      if (document.activeElement) {
        const indexOfActiveElement = arrayOfFocusableElements.indexOf(
          document.activeElement as HTMLElement
        );
        if (arrayOfFocusableElements[indexOfActiveElement - 1] !== undefined) {
          arrayOfFocusableElements[indexOfActiveElement - 1].focus();
        }
        return;
      }
    }
  }
  if (event.key === 'ArrowDown') {
    const parent = document.getElementById('ac-result-list') as HTMLElement;
    if (parent) {
      const theList = getAllFocusableElements(parent);
      const arrayOfFocusableElements = Array.from(theList) as HTMLElement[];
      if (document.activeElement) {
        const indexOfActiveElement = arrayOfFocusableElements.indexOf(
          document.activeElement as HTMLElement
        );
        if (arrayOfFocusableElements[indexOfActiveElement + 1] !== undefined) {
          arrayOfFocusableElements[indexOfActiveElement + 1].focus();
        }
        return;
      }
    }
  }
}
const getAllFocusableElements = (parent: any) => {
  if (!parent) {
    console.warn('You need to pass a parent HTMLElement');
    return []; // Return array so length queries will work
  }

  return parent.querySelectorAll(
    '[href], [tabindex]:not([tabindex="-1"]):not([disabled])'
  );
};

export function focusOnInput(): void {
  const searchInputField = document.getElementById('searchfield-input');
  searchInputField?.focus();
}

export const useUnload = (fn: any) => {
  const cb = useRef(fn); // init with fn, so that type checkers won't assume that current might be undefined

  useEffect(() => {
    cb.current = fn;
  }, [fn]);

  useEffect(() => {
    const onUnload = (...args: any) => cb.current?.(...args);

    window.addEventListener('beforeunload', onUnload);

    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
};

export default useUnload;
export function getDates(todayDate: boolean): string {
  let returnDate = '';
  const date = new Date();

  if (todayDate === true) {
    const today = date.toJSON().slice(0, 10);
    returnDate = today;
  } else {
    date.setFullYear(date.getFullYear() + 10);
    const futureTime = date.toJSON().slice(0, 10);
    returnDate = futureTime;
  }
  return returnDate;
}

//Ugly way of distinguish only letters for immediate validation, strict mode.
export function restrictToOnlyLetters(
  e: React.KeyboardEvent<HTMLInputElement>,
  lettersOnly: boolean | undefined
): void {
  if (
    lettersOnly &&
    (isFinite(Number(e.key)) ||
      e.key === '.' ||
      e.key === ',' ||
      e.key === '!' ||
      e.key === '#' ||
      e.key === '€' ||
      e.key === '%' ||
      e.key === '&' ||
      e.key === '/' ||
      e.key === '(' ||
      e.key === ')' ||
      e.key === '=' ||
      e.key === '?' ||
      e.key === '¡' ||
      e.key === '”' ||
      e.key === '¥' ||
      e.key === '¢' ||
      e.key === '‰' ||
      e.key === '¶' ||
      e.key === '{' ||
      e.key === '}' ||
      e.key === '≠' ||
      e.key === '_' ||
      e.key === ':' ||
      e.key === ';' ||
      e.key === '*' ||
      e.key === '¨' ||
      e.key === '<' ||
      e.key === '>' ||
      e.key === '≤' ||
      e.key === '≥' ||
      e.key === '¿' ||
      e.key === '`') &&
    e.key !== 'Backspace' &&
    e.key !== 'Tab' &&
    e.key !== 'ArrowRight' &&
    e.key !== 'ArrowLeft'
  ) {
    e.preventDefault();
  }
}

export const translateEstateType = (value: string) => {
  switch (value) {
    case 'Villa':
      return 'liten_villa.svg';
    case 'Tomt':
      return 'liten_tomt.svg';
    case 'Kommersiellt':
      return 'liten_kommersiell.svg';
    case 'Fritidshus':
      return 'liten_fritidshus.svg';
    case EstateType.Cooperative:
      return 'liten_bostadsratt.svg';
    case 'Gård':
      return 'liten_gard.svg';
    case 'Ägarlägenhet':
      return 'liten_bostadsratt.svg';
    default:
      return 'liten_villa.svg';
  }
};

export function GetStatusText(
  id: string,
  isSeller: boolean,
  accepted: boolean,
  showOnMyPages: boolean
) {
  if (StatusId.ForSale == id || StatusId.HeadStartHomes == id) {
    return !isSeller ? 'Till salu' : 'Försäljning pågår';
  } else if (StatusId.Intake == id) {
    return !isSeller
      ? 'Ej under publik försäljning'
      : accepted
      ? 'Godkänd, väntar på mäklare'
      : showOnMyPages
      ? 'Väntar på godkännande'
      : 'Mäklaren förbereder annonsen';
  } else if (StatusId.Sold == id) {
    return !isSeller ? 'Såld' : 'Sålt';
  } else if (StatusId.DuringIntake == id) {
    return 'Kontakt med mäklare påbörjad';
  } else if (StatusId.Coming == id) {
    return 'Kommande försäljning';
  }
  return '';
}
export function municipalityCheck(value: string | undefined | null) {
  switch (value) {
    case 'Gotland':
      return 'på';
    case 'Lidingö':
      return 'på';
    case 'Öland':
      return 'på';
    case 'Ekerö':
      return 'på';
    case 'Öckerö':
      return 'på';
    case 'Orust':
      return 'på';
    case 'Mörbylånga':
      return 'på';
    case 'Tjörn':
      return 'på';
    case 'Värmdö':
      return 'på';
    case 'Hammarö':
      return 'på';
    default:
      return 'i';
  }
}

/**
 * This function is the same as in UserItem.cs, matches the old get current estate on user.
 * @param user
 * @param estateId
 * @returns MyPagesEstateDto
 */
export function GetCurrentEstate(
  user: User,
  estateId?: string
): MyPagesEstateDto {
  if (estateId) {
    return (
      user?.objectsImBuying.find((obj) => obj.id === estateId) ??
      user?.objectsImSelling.find((obj) => obj.id === estateId)
    );
  }
  return (
    user?.objectsImSelling.sort(
      (a: MyPagesEstateDto, b: MyPagesEstateDto) => a.sortIndex - b.sortIndex
    )[0] ?? user?.objectsImBuying[0]
  );
}

export const letterTransform = (name: string) => {
  return name?.replace(/[åäö]/gi, (match) => {
    switch (match) {
      case 'å':
        return 'a';
      case 'ä':
        return 'a';
      case 'ö':
        return 'o';
    }
  });
};

export const getSessionId = () => {
  return Cookies.get('sessionId');
};

export const removeSessionId = () => {
  Cookies.remove('sessionId');
};
export const removeLoginType = () => {
  Cookies.remove('loginType');
};

export function isNullOrWhiteSpace(str: string) {
  return str === null || str.match(/^ *$/) !== null;
}

export const officeBrokerGrayScale = (office: string | undefined | null) => {
  switch (office) {
    case 'Sollentuna':
      return true;
    case 'Sundbyberg':
      return true;
    case 'Solna':
      return true;
    case 'Österåker':
      return true;
    default:
      return false;
  }
};

export const arealTerms = (value: string) => {
  switch (value) {
    case 'forest':
      return 'Skog';
    case 'wasteLand':
      return 'Impediment';
    case 'arabel':
      return 'Åker';
    case 'infield':
      return 'Inägomark';
    case 'pasture':
      return 'Bete';
    case 'water':
      return 'Vatten';
    case 'other':
      return 'Övrigt';
    case 'totalArea':
      return 'Totalt';
    case 'Skogsbruksplan':
      return 'Areal enligt skogsbruksplan';
    default:
      return value;
  }
};

export const CountUp = (endValue: number, duration: number) => {
  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    let startTime: number | null = null;
    let animationFrame: number;

    const startAnimation = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(1, elapsedTime / duration);
      const newValue = Math.floor(progress * endValue);
      setCurrentValue(newValue);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(startAnimation);
      }
    };

    animationFrame = requestAnimationFrame(startAnimation);

    return () => cancelAnimationFrame(animationFrame);
  }, [endValue, duration]);
};

export const scrollToElementById = (e: string) => {
  const element = document.getElementById(e);
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

export const postalCodeFormatter = (str: string): string => {
  if (str.length < 3) {
    return str;
  }
  const part1 = str.slice(0, 3);
  const part2 = str.slice(3);
  return `${part1} ${part2}`;
};
export function scrollToFunction(
  elementId: string,
  behavior: ScrollBehavior = 'smooth',
  position: ScrollLogicalPosition = 'center'
) {
  const element = document.getElementById(elementId)?.firstChild as HTMLElement;
  element?.scrollIntoView({
    behavior: behavior,
    block: position,
  });
}
export const capitalizeFirstLetter = (str: string) => {
  if (!str) {
    return '';
  }
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const buildUmbracoImageUrl = (
  url: string,
  crop: Crop,
  focalPoint: FocalCoordinates,
  fallbackWidth: number,
  fallBackHeight: number
) => {
  const width = crop?.width ?? fallbackWidth;
  const height = crop?.height ?? fallBackHeight;

  if (crop?.coordinates) {
    return `${url}?cc=${crop?.coordinates?.x1},${crop?.coordinates?.y1},${crop?.coordinates?.x2},${crop?.coordinates?.y2}&width=${width}&height=${height}&format=webp`;
  } else if (focalPoint) {
    return `${url}?rxy=${focalPoint.left},${focalPoint?.top}&width=${width}&height=${height}&format=webp`;
  } else {
    return `${url}?width=${width}&height=${height}&format=webp`;
  }
};

export const getUmbracoImagePosition = (focalPoint: FocalCoordinates) => {
  if (focalPoint) {
    return {
      objectPosition: `${focalPoint.left * 100}% ${focalPoint.top * 100}%`,
    };
  }
};

export const parseJsonResponse = <T>(resp: any): T => {
  return typeof resp === 'string' ? JSON.parse(resp) : resp;
};

// Utility function to set a flag with expiration in sessionStorage
export const setSessionFlagWithExpiry = (
  key: string,
  value: string,
  expiryTimeMs: number
) => {
  const expiryTimestamp = Date.now() + expiryTimeMs;
  const data = { value, expiryTimestamp };
  sessionStorage.setItem(key, JSON.stringify(data));
};

// Utility function to get a flag and check its expiration
export const getSessionFlagWithExpiry = (key: string): string | null => {
  const data = sessionStorage.getItem(key);
  if (!data) return null;

  const { value, expiryTimestamp } = JSON.parse(data);

  // Check if the current time is past the expiration time
  if (Date.now() > expiryTimestamp) {
    sessionStorage.removeItem(key); // Invalidate the expired key
    return null;
  }

  return value;
};

export const isMarkerInsidePolygon = (
  markerLatLng: { lat: number; lng: number }, // Update parameter type to { lat, lng }
  poly: { lat: number; lng: number }[][] // Update polygon type to match new format
): boolean => {
  // Convert markerLatLng to L.LatLng
  const marker = L.latLng(markerLatLng.lat, markerLatLng.lng);

  // Convert polygon points to L.LatLng[]
  const polyPoints = poly[0].map((point) => L.latLng(point.lat, point.lng));

  const x = marker.lat,
    y = marker.lng;

  let inside = false;
  for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
    const xi = polyPoints[i].lat,
      yi = polyPoints[i].lng;
    const xj = polyPoints[j].lat,
      yj = polyPoints[j].lng;

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
};
